// Ported from dev-scrips-pm-react src/documents/components/document-list.tsx (PROD-1119 documents module).

import { Banner, Button, EmptyState, Skeleton } from '@scripsteam/scrips-signal-ds';
import { PatientDocumentRow } from './document-row';
import type { PatientDocumentModel } from '@/documents/types/document';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * DocumentList — generic list view for patient documents (PROD-797).
 *
 * Pure presentational — caller wires the data hook. Used by:
 *   - ViewAppointmentPanel Docs tab (this PR)
 *   - Future patient-chart Docs tab (reuses without changes)
 *
 * V1 ships read-only rows (DocumentRow). Mark-reviewed / edit /
 * delete actions land in subsequent PROD-797 PRs once each mutation's
 * dev curl is captured.
 *
 * States: loading skeleton (4 rows), error message, empty state with
 * a friendly hint, populated list.
 */

interface DocumentListProps {
  documents: PatientDocumentModel[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Optional empty-state message override (e.g. "No documents for this encounter yet"). */
  emptyMessage?: string;
  /** `card` (default) = the Chart's dashed empty box. `bare` = the deployed encounter step's
   *  centred sentence with no chrome (`document_list_widget.dart:49-56`). */
  emptyVariant?: 'card' | 'bare';
  /** Forwarded to every row — see `PatientDocumentRow`'s `surface`. `encounter-step` draws the
   *  deployed encounter Docs card (Flutter `document_list_widget.dart`), `chart` the DS row. */
  surface?: 'chart' | 'encounter-step';
  /** The documents query's refetch. When given, the error Banner carries a "Try again" button that calls it. */
  onRetry?: () => void;
}

export function DocumentList({
  documents,
  isLoading,
  isError,
  emptyMessage = 'No documents yet.',
  emptyVariant = 'card',
  surface = 'chart',
  onRetry,
}: DocumentListProps) {
  if (isLoading) {
    // Skeleton mirrors the DS DocumentRow anatomy (separator-delimited rows,
    // 56px right thumbnail) so the loaded list doesn't jump.
    return (
      <ul className="m-0 list-none p-0" aria-busy>
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="flex items-start gap-4 border-b border-border-default pb-4 pt-4"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-2 w-2/5" />
            </div>
            <Skeleton className="h-14 w-14 rounded-lg" />
          </li>
        ))}
      </ul>
    );
  }

  if (isError) {
    return (
      // The design's error banner (PaDocumentList, 21-10): the DS Banner, title and the line, with "Try again" (the
      // query's refetch) when the caller passes one.
      <Banner
        variant="error"
        role="alert"
        title="Couldn't load documents"
        action={
          onRetry ? (
            <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
      >
        Refresh the list to try again.
      </Banner>
    );
  }

  const rows = documents ?? [];
  if (rows.length === 0) {
    // `bare` = the deployed encounter step's empty state: a centred `Text` at 15/w400 in the
    // muted grey with 100px of tail, and no chrome at all
    // (`document_list_widget.dart:49-56`). The Chart keeps its dashed box + glyph, which is
    // its own surface's anatomy, not drift.
    return emptyVariant === 'bare' ? (
      <p className="pb-25 pt-2 text-center text-body-15 leading-body text-text-muted">
        {emptyMessage}
      </p>
    ) : (
      // The design's empty list (PaDocumentList): the DS EmptyState with the file glyph ("No archived documents.", filtered).
      <EmptyState icon="file" title={emptyMessage} />
    );
  }

  return (
    // DS DocumentRow rows are separator-delimited <li>s (E1 anatomy, PROD-1588
    // wave 4 L7) — the wrapper is the DS DocumentsSection's own bare-list shape.
    // ROW RHYTHM: Flutter's `ListView.separated` puts `verticalSpace8` between every pair
    // (`document_list_widget.dart:46-47`) on BOTH surfaces — the same widget serves the chart
    // tab — so the gap is unconditional. The CARD anatomy around each row is a separate,
    // frozen-anchor question and is deliberately NOT touched here.
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {rows.map((doc) => (
        <PatientDocumentRow
          key={doc.id ?? `${doc.name}-${doc.documentDate ?? ''}`}
          document={doc}
          surface={surface}
        />
      ))}
    </ul>
  );
}
